<!--
  ReconTab.vue
  콜봇 월 정산 · 병원 상세 `대사` 탭(?tab=recon) — 화면 명세 2-2 건수 대사(vox ↔ DB)와
  "vox 원가와 AI 시간 대사"(ReconCostPanel.vue).

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2 › 대사 탭", "요청 계약 5"(건수 대사),
        "요청 계약 7"(COUNT_MISMATCH·NOT_FINAL_CALLS — 차단 여부는 경고 객체의 blocking)
  실제 구현(~/md/front/admin-콜봇-월정산-call-api-구현회신.md 표 3·1, B17):
  - 판정은 웹 테스트를 뺀 건수끼리다(`db_non_web_total` ↔ `vox_live_non_web_total`, 허용 오차 0). 웹콜은 DB 미러에
    없어 전체 건수(`db_total`·`vox_live_total`)는 참고로만 보인다 — vox 전체는 웹 요약이 완전할 때만 값이 있다.
  - call-api 는 월말 뒤에 읽은 vox 값으로만 COUNT_MISMATCH 를 낸다. 그 전에 읽은 값이면 판정에 "월말 전 값"을 단다.
  - 누락 통화 ID 를 못 받으면(`missing: null`) 그 이유(`missing_unavailable_reason`)를 보인다.
  - WEB_USAGE_UNVERIFIED(웹 사용량 미확인)가 있으면 [vox 건수 확인]이 웹 요약도 다시 읽는다고 알리고, 읽은 뒤
    검토 필요 정산이면 정산 탭 [다시 계산]으로 보낸다.
  데이터
  - 셸이 받은 정산 상세(`detail`)의 reconciliation·usage.calls·warnings·statement 만 쓴다. 탭을 열어도
    따로 부르지 않는다(vox 는 연속 약 20회 호출에서 429 — "왜 목록은 DB 집계만 쓰는가").
  - vox 를 부르는 건수 대사(GET .../billing/reconciliation)는 [vox 건수 확인]·[누락 통화 ID 확인]을 누를
    때만 부른다. call-api 가 읽은 건수를 캐시해 COUNT_MISMATCH 판정에 쓰므로 끝나면 상세를 다시 불러온다.
  - 셸 intent `check-missing-ids`(정산 탭 COUNT_MISMATCH 의 해결 링크) → 건수 확인 → 불일치일 때만 누락 ID 확인
    → intent-done. 지금 건수가 일치하면 누락 ID 를 훑지 않는다(vox 통화 ID 를 페이지로 읽는 호출 — 2-2 도 일치면
    [누락 통화 ID 확인]이 없다). 그때는 남은 경고가 이전 건수로 판정한 것이라는 안내가 보인다.
  - 셸 intent `check-count`(정산 탭 WEB_USAGE_UNVERIFIED 의 해결 링크) → 건수 확인(웹 요약까지) → intent-done.
  - 목(?settlementMock=1)이면 대사 장면(일치·불일치·429·409)을 고르는 개발용 조작을 보인다.
  셸 ↔ 탭 계약(props·emits)은 네 탭이 같다(SettlementDetail.vue). 탭은 병원·달마다 한 번 마운트된다(KeepAlive).
  셸은 이 병원·달의 정산 상세가 온 뒤에만 이 탭을 그린다 — `detail` 은 늘 이 병원·달 것이고 null 이 아니다
  (첫 로드·실패·다시 시도는 셸 몫).
-->
<template>
  <div class="recon-tab">
    <div v-if="mock" class="recon-tab__dev">
      <SettlementMockBadge>
        <span>대사 장면</span>
        <a-select
          size="small"
          class="recon-tab__dev-select"
          :value="reconScenario"
          :options="RECON_OPTIONS"
          aria-label="건수 대사 장면(목)"
          @change="onScenarioChange"
        />
        <span>다음 [vox 건수 확인]부터 적용</span>
      </SettlementMockBadge>
    </div>

    <!-- 2-2. 건수 대사 (vox ↔ DB) -->
    <section ref="countSection" class="recon-card" :aria-labelledby="countTitleId">
      <header class="recon-card__head">
        <h2 :id="countTitleId" class="recon-card__title">통화 건수 대사 (vox ↔ DB)</h2>
        <span class="recon-verdict" :class="`is-${verdict.state}`" role="status">
          <i v-if="verdict.state === 'match'" class="fa-solid fa-check" aria-hidden="true"></i>
          {{ verdict.label }}
        </span>
        <div class="recon-card__actions">
          <a-button :loading="countLoading" :disabled="busy && !countLoading" @click="checkCount">
            vox 건수 확인
          </a-button>
          <a-button
            v-if="verdict.state === 'mismatch'"
            type="primary"
            class="yellow-button"
            :loading="missingLoading"
            :disabled="busy && !missingLoading"
            @click="checkMissingIds"
          >
            누락 통화 ID 확인
          </a-button>
        </div>
      </header>
      <p class="recon-card__desc">
        vox 는 이 버튼을 누를 때만 부릅니다(호출 한도). 판정은 웹 테스트를 뺀 DB 통화 수와 vox 건수로만 하고, 마감
        판정에는 월말 뒤에 읽은 vox 값만 씁니다. [vox 건수 확인]은 웹 테스트 요약도 함께 다시 읽습니다.
      </p>

      <a-alert
        v-if="checkError"
        class="recon-card__alert"
        :type="checkError.type"
        show-icon
        :message="checkError.text"
      />

      <div class="recon-tiles">
        <div class="recon-tile">
          <span class="recon-tile__label">DB 통화 수(웹 제외)</span>
          <span class="recon-tile__value">{{ formatCount(numbers.dbNonWeb, '건') }}</span>
          <span class="recon-tile__note">{{ dbNote }}</span>
        </div>
        <div class="recon-tile" :class="{ 'is-empty': numbers.voxNonWeb === null }">
          <span class="recon-tile__label">vox 라이브 건수(웹 제외)</span>
          <span class="recon-tile__value">{{ formatCount(numbers.voxNonWeb, '건') }}</span>
          <span class="recon-tile__note" :title="voxNote.title">{{ voxNote.text }}</span>
          <span v-if="voxTotalNote" class="recon-tile__note">{{ voxTotalNote }}</span>
        </div>
        <div class="recon-tile is-reference">
          <span class="recon-tile__label">
            청구서 통화 요금 수량(참고)
            <span class="recon-pill is-reference">참고 — 단위 확인 중</span>
          </span>
          <span class="recon-tile__value">{{ formatNumber(numbers.invoiceQty) }}</span>
          <span class="recon-tile__note">
            {{ numbers.invoiceQty === null ? 'vox 청구서가 나온 뒤에 보입니다.' : '단위(건수/분)를 확인하기 전이라' }}
            판정에 쓰지 않습니다.
          </span>
        </div>
      </div>

      <!-- COUNT_MISMATCH — 방금 읽은 판정과 정산에 남은 경고를 함께 본다 -->
      <div
        v-if="countCallout"
        class="recon-callout"
        :class="countCallout.blocking ? 'is-danger' : 'is-muted'"
        role="note"
      >
        <div class="recon-callout__head">
          <strong>{{ COUNT_MISMATCH_LABEL }}</strong>
          <code class="recon-code">COUNT_MISMATCH</code>
          <span class="recon-pill" :class="countCallout.blocking ? 'is-danger' : 'is-muted'">
            {{ countCallout.blocking ? '차단' : '비차단' }}
          </span>
        </div>
        <p class="recon-callout__text">{{ countCallout.text }}</p>
        <p v-if="countCallout.next" class="recon-callout__next">
          <span>{{ countCallout.next.text }}</span>
          <button
            v-if="countCallout.next.action === 'recalculate'"
            type="button"
            class="recon-link"
            @click="goRecalculate"
          >
            정산 탭에서 다시 계산 →
          </button>
          <a-button v-else-if="countCallout.next.action === 'reopen'" size="small" @click="emit('open-reopen')">
            재수정
          </a-button>
        </p>
      </div>

      <!-- WEB_USAGE_UNVERIFIED — 웹 테스트는 vox 웹 요약으로만 청구한다(회신 표 1) -->
      <div v-if="webCallout" class="recon-callout" :class="webCallout.blocking ? 'is-danger' : 'is-muted'" role="note">
        <div class="recon-callout__head">
          <strong>{{ WEB_UNVERIFIED_LABEL }}</strong>
          <code class="recon-code">WEB_USAGE_UNVERIFIED</code>
          <span class="recon-pill" :class="webCallout.blocking ? 'is-danger' : 'is-muted'">
            {{ webCallout.blocking ? '차단' : '비차단' }}
          </span>
        </div>
        <p class="recon-callout__text">{{ webCallout.text }}</p>
        <p v-if="webCallout.next" class="recon-callout__next">
          <span>{{ webCallout.next.text }}</span>
          <button v-if="webCallout.next.action === 'recalculate'" type="button" class="recon-link" @click="goRecalculate">
            정산 탭에서 다시 계산 →
          </button>
          <a-button v-else-if="webCallout.next.action === 'reopen'" size="small" @click="emit('open-reopen')">
            재수정
          </a-button>
        </p>
      </div>

      <!-- 누락 통화 ID 확인 결과 -->
      <div v-if="missingResult" ref="missingBlock" class="recon-missing" tabindex="-1">
        <div class="recon-missing__head">
          <span class="recon-missing__title">누락 통화 ID 확인 결과</span>
          <span class="recon-missing__meta">{{ missingResult.meta }}</span>
        </div>
        <p v-if="missingResult.unavailable" class="recon-missing__empty">{{ missingResult.unavailableText }}</p>
        <p v-else-if="!missingResult.rows.length" class="recon-missing__empty is-ok">
          <i class="fa-solid fa-check" aria-hidden="true"></i>
          누락 없음 — vox 에만 0건 · DB 에만 0건
        </p>
        <template v-else>
          <p class="recon-missing__summary">
            vox 에만 <b>{{ formatNumber(missingResult.voxOnly) }}건</b> · DB 에만
            <b>{{ formatNumber(missingResult.dbOnly) }}건</b>
            <span class="recon-missing__hint">웹 테스트 제외</span>
          </p>
          <a-table
            class="recon-table"
            size="small"
            row-key="key"
            :columns="MISSING_COLUMNS"
            :data-source="missingResult.rows"
            :pagination="false"
          >
            <template #bodyCell="{ column, record }">
              <span
                v-if="column.key === 'side'"
                class="recon-pill"
                :class="record.side === 'vox' ? 'is-warn' : 'is-accent'"
              >
                {{ record.sideLabel }}
              </span>
              <code v-else-if="column.key === 'id'" class="recon-id">{{ record.id }}</code>
            </template>
          </a-table>
        </template>
      </div>

      <!-- DB 분해 막대 -->
      <div class="recon-breakdown">
        <div class="recon-breakdown__head">
          <span class="recon-breakdown__title">DB 통화 분해</span>
          <span class="recon-breakdown__meta">{{ formatCount(breakdown.total, '건') }} · 유형별</span>
        </div>
        <div v-if="breakdown.sum > 0" class="recon-bar" role="img" :aria-label="breakdown.ariaLabel">
          <span
            v-for="part in breakdown.visible"
            :key="part.key"
            class="recon-bar__seg"
            :class="`is-${part.key}`"
            :style="{ width: `${part.pct}%` }"
            :title="`${part.label} ${formatNumber(part.n)}건`"
          ></span>
        </div>
        <p v-else class="recon-breakdown__empty">이 달 DB 통화가 없습니다.</p>
        <ul class="recon-legend">
          <li v-for="part in breakdown.parts" :key="part.key" class="recon-legend__item" :title="part.tip">
            <span class="recon-legend__swatch" :class="`is-${part.key}`" aria-hidden="true"></span>
            <span>{{ part.label }}</span>
            <b class="recon-legend__count">{{ formatNumber(part.n) }}</b>
            <span v-if="part.sub" class="recon-legend__sub">{{ part.sub }}</span>
          </li>
        </ul>
      </div>

      <!-- 미종료 통화 -->
      <div class="recon-notfinal">
        <span class="recon-chip" :class="`is-${notFinalView.tone}`">
          미종료 통화 <b>{{ formatCount(notFinalView.n, '건') }}</b>
        </span>
        <template v-if="notFinalView.n > 0">
          <span class="recon-notfinal__text">{{ notFinalView.text }}</span>
          <code class="recon-code">NOT_FINAL_CALLS</code>
          <span class="recon-pill" :class="notFinalView.blocking ? 'is-danger' : 'is-muted'">
            {{ notFinalView.blocking ? '차단' : '비차단' }}
          </span>
          <button type="button" class="recon-link" @click="goWarnings">정산 탭에서 경고 보기 →</button>
        </template>
      </div>

      <p class="recon-footnote">{{ MONTH_EDGE_NOTE }}</p>
    </section>

    <ReconCostPanel :detail="detail" />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onActivated, onBeforeUnmount, ref, watch } from 'vue';

import SettlementMockBadge from '../SettlementMockBadge.vue';
import ReconCostPanel from './ReconCostPanel.vue';
import {
  getCallApiErrorBody,
  getCallApiErrorCode,
  getCallApiErrorStatus,
  isCallApiUnimplemented,
  parseCallApiError,
} from '@/views/Callbot/service/callApiError';
import {
  formatCount,
  formatKstDateTime,
  formatKstTime,
  formatNumber,
  isNumber,
  relativeKstDayLabel,
} from '../../service/settlementFormat';
import {
  WARNING_META,
  webSourceView,
  WEB_USAGE_REASON_TEXT,
  isInternalDetail,
  reconErrorText,
  reconMissingUnavailableText,
  reconStatus,
  statementStatusOf,
} from '../../service/settlementRules';
import { settlementMockControls } from '../../service/settlementMock';

const props = defineProps({
  companyId: { type: String, required: true },
  /** 'YYYY-MM'(KST) */
  month: { type: String, required: true },
  /** 정산 상세(요청 계약 2) — 셸이 이 병원·달 상세가 온 뒤에만 이 탭을 그려 null 이 아니다 */
  detail: { type: Object, required: true },
  /** 상세를 다시 받는 중 — 셸 ↔ 탭 계약이라 받기만 한다(다시 받는 동안에도 셸이 이전 상세를 넘긴다) */
  detailLoading: { type: Boolean, default: false },
  /** settlementApi 또는 같은 모양의 목(getSettlementApi()) */
  api: { type: Object, required: true },
  mock: { type: Boolean, default: false },
  /** 정산 상세를 다시 부른다 — 건수 대사 뒤에 부른다(캐시한 vox 건수가 COUNT_MISMATCH 를 바꾼다) */
  reload: { type: Function, required: true },
  /** 셸이 이 탭에 보낸 지시 { type, payload?, nonce } — 'check-missing-ids' | 'check-count' */
  intent: { type: Object, default: null },
});

const emit = defineEmits(['go-tab', 'open-reopen', 'intent-done']);

/** 화면 명세 2-2 고정 각주 */
const MONTH_EDGE_NOTE =
  '월말 자정 근처 통화는 vox 와 우리가 다른 달로 셀 수 있습니다(vox 는 연결 전 통화를 생성 시각, 우리는 종료 시각 기준).';
const COUNT_MISMATCH_LABEL = WARNING_META.COUNT_MISMATCH.label;
const WEB_UNVERIFIED_LABEL = WARNING_META.WEB_USAGE_UNVERIFIED.label;
const UNIMPLEMENTED_TEXT = '건수 대사 API 가 이 환경에 아직 없습니다.';

const MISSING_COLUMNS = [
  { title: '누락 쪽', key: 'side', dataIndex: 'sideLabel' },
  { title: '통화 ID', key: 'id', dataIndex: 'id' },
  { title: '일시(KST)', key: 'at', dataIndex: 'at' },
  { title: '유형', key: 'type', dataIndex: 'type' },
];

/** vox 통화 유형(call-api VoxCallType) */
const CALL_TYPE_LABELS = { inbound: '수신', outbound: '발신', web: '웹 테스트', phone: '전화', api: 'API' };

// 얼린 공유 상수라 a-select 에는 복사본을 넘긴다(DetailHeader 의 마감 상태 고르기와 같다)
const RECON_OPTIONS = settlementMockControls.RECON_OPTIONS.map((option) => ({ ...option }));

const countTitleId = `st-recon-count-${getCurrentInstance()?.uid ?? 0}`;
const countSection = ref(null);
const missingBlock = ref(null);

const num = (v) => (isNumber(v) ? v : null);
const firstNumber = (...values) => values.find((v) => isNumber(v)) ?? null;

// ---------------------------------------------------------------------------
// 상세 — 셸이 넘긴 이 병원·이 달 상세 하나(머리말)
// ---------------------------------------------------------------------------

const status = computed(() => statementStatusOf(props.detail));
const internal = computed(() => isInternalDetail(props.detail));

function findWarning(code) {
  const list = props.detail.warnings;
  return Array.isArray(list) ? list.find((w) => w?.code === code) ?? null : null;
}

/** 차단 여부는 경고 객체가 정한다. 경고가 아직 없으면 내부·테스트(모두 비차단)만 비차단으로 본다 */
function blockingOf(warning) {
  return warning ? warning.blocking === true : !internal.value;
}

// ---------------------------------------------------------------------------
// 건수 — 버튼으로 막 읽은 값이 있으면 그 값, 없으면 상세(자동 마감 잡·이전 확인이 캐시한 값)
// ---------------------------------------------------------------------------

const countLoading = ref(false);
const missingLoading = ref(false);
const busy = computed(() => countLoading.value || missingLoading.value);
/**
 * 마지막 건수 대사 응답 { db_total, db_non_web_total, vox_live_total, vox_live_non_web_total, vox_live_fetched_at,
 * web_source, invoice_call_usage_quantity, missing, missing_unavailable_reason, cached }
 */
const lastCheck = ref(null);
/** 마지막 누락 ID 확인 결과(화면용으로 바꾼 것) */
const missingResult = ref(null);
/** 건수 대사 실패 { text, type } */
const checkError = ref(null);

/**
 * 대사 숫자 — 판정은 웹 제외(dbNonWeb ↔ voxNonWeb), 전체(dbTotal·voxTotal)는 참고. 상세의 `reconciliation` 은
 * 검토 필요·확정 달이면 마감 시점 스냅샷이다(회신 B12).
 */
const numbers = computed(() => {
  const base = props.detail.reconciliation ?? {};
  const fresh = lastCheck.value;
  const pick = (src, fallback) => ({
    dbNonWeb: firstNumber(src.db_non_web_total, fallback.db_non_web_total),
    voxNonWeb: num(src.vox_live_non_web_total),
    dbTotal: firstNumber(src.db_total, fallback.db_total, props.detail.usage?.calls?.total),
    voxTotal: num(src.vox_live_total),
    fetchedAt: num(src.vox_live_fetched_at),
    webSource: src.web_source ?? fallback.web_source ?? props.detail.usage?.web_source ?? null,
    invoiceQty: firstNumber(src.invoice_call_usage_quantity, fallback.invoice_call_usage_quantity),
  });
  if (fresh) return { ...pick(fresh, base), source: fresh.cached === true ? 'cached' : 'fresh' };
  return { ...pick(base, {}), source: 'stored' };
});

const verdict = computed(() =>
  reconStatus(numbers.value.dbNonWeb, numbers.value.voxNonWeb, { fetchedAt: numbers.value.fetchedAt, month: props.month })
);

/** DB 타일 보조 — 웹 테스트 포함 전체(참고). 웹 건수는 vox 웹 요약에서 온다(없으면 DB 미러 — 웹콜이 없다) */
const dbNote = computed(() => {
  const { dbTotal, webSource } = numbers.value;
  const total = isNumber(dbTotal) ? `웹 포함 ${formatCount(dbTotal, '건')}(참고)` : '';
  return ['우리 DB 통화 미러', total, webSourceView(webSource).note].filter(Boolean).join(' · ');
});

/** vox 타일 보조 — 웹 포함 전체는 웹 요약을 끝까지 읽었을 때만 있다 */
const voxTotalNote = computed(() => {
  const { voxNonWeb, voxTotal } = numbers.value;
  if (voxNonWeb === null) return '';
  return isNumber(voxTotal) ? `웹 포함 ${formatCount(voxTotal, '건')}(참고)` : '웹 포함 — (웹 요약을 끝까지 읽지 못함)';
});

/** '조회 14:05'(오늘) 또는 '조회 2026-10-03 06:00' */
function fetchedText(ms) {
  if (!isNumber(ms)) return '';
  return `조회 ${relativeKstDayLabel(ms) === '오늘' ? formatKstTime(ms) : formatKstDateTime(ms)}`;
}

const voxNote = computed(() => {
  // 웹 제외 건수로 정한다 — 웹 포함 전체(vox_live_total)는 웹 요약을 끝까지 못 읽으면 건수를 읽었어도 null 이다
  const { voxNonWeb, fetchedAt, source } = numbers.value;
  if (voxNonWeb === null) {
    return { text: '아직 읽지 않았습니다 — [vox 건수 확인]으로 불러옵니다.', title: '' };
  }
  const when = fetchedText(fetchedAt) || '조회 시각 모름';
  if (source === 'cached') {
    return { text: `${when} · 캐시`, title: 'call-api 가 보관한 건수입니다(vox 를 다시 부르지 않았습니다).' };
  }
  if (source === 'stored') {
    return { text: `${when} · 저장된 값`, title: '자동 마감 잡이나 이전 확인이 읽어 둔 건수입니다.' };
  }
  return { text: when, title: '' };
});

const countCallout = computed(() => {
  const warning = findWarning('COUNT_MISMATCH');
  const state = verdict.value.state;
  if (state !== 'mismatch' && !warning) return null;

  // 월말 전에 읽은 값의 차이는 경고가 되지 않는다(진행 중인 달은 통화가 계속 들어온다)
  const provisional = state === 'mismatch' && !verdict.value.final;
  const blocking = provisional && !warning ? false : blockingOf(warning);
  let text;
  if (provisional) {
    const n = formatNumber(Math.abs(verdict.value.diff));
    text = `웹 테스트를 뺀 vox 건수와 DB 통화 수가 지금 ${n}건 다릅니다. 월말 전에 읽은 값이라 판정하지 않습니다 — 달이 끝난 뒤 마감이 vox 를 다시 읽어 판정합니다.`;
    if (!missingResult.value) text += ' 어느 통화인지 미리 보려면 [누락 통화 ID 확인]을 누릅니다.';
  } else if (state === 'mismatch') {
    const n = formatNumber(Math.abs(verdict.value.diff));
    text = `웹 테스트를 뺀 vox 건수와 DB 통화 수가 ${n}건 다릅니다. DB 에 빠진 통화가 있으면 덜 청구됩니다.`;
    text += blocking ? ' 확정 전 확인이 필요합니다.' : ' 내부·테스트 회사라 기록만 하고 마감을 막지 않습니다.';
    if (!missingResult.value) text += ' [누락 통화 ID 확인]으로 어느 통화인지 봅니다.';
  } else if (state === 'match') {
    text = '정산에 남은 이 경고는 이전 건수로 판정한 것입니다. 지금 읽은 건수(웹 제외)는 일치합니다.';
  } else {
    text = '정산이 웹 테스트를 뺀 vox 건수와 DB 통화 수가 다르다고 기록했습니다. [vox 건수 확인]으로 지금 건수를 읽습니다.';
  }

  // 검토 필요·확정 달의 경고는 정산 스냅샷이다 — 방금 읽은 판정과 다르면 반영하는 길을 알린다
  let next = null;
  const snapshotStale = state !== 'unknown' && !provisional && !!warning !== (state === 'mismatch');
  if (snapshotStale && status.value === 'review') {
    next = { action: 'recalculate', text: '검토 필요 정산의 경고는 [다시 계산]해야 이 결과로 바뀝니다.' };
  } else if (snapshotStale && status.value === 'confirmed') {
    next = { action: 'reopen', text: '확정된 정산은 바뀌지 않습니다. 반영하려면 재수정합니다.' };
  }
  return { blocking, text, next };
});

/**
 * WEB_USAGE_UNVERIFIED — 끝난 달인데 월말 뒤에 끝까지 읽은 vox 웹 요약이 없다. [vox 건수 확인]이 웹 요약까지 읽고,
 * 검토 필요 정산은 [다시 계산]해야 반영된다(추정 중은 상세를 다시 받으면 바로, 확정은 재수정).
 */
const webCallout = computed(() => {
  const warning = findWarning('WEB_USAGE_UNVERIFIED');
  if (!warning) return null;
  const blocking = blockingOf(warning);
  const reason = warning.details && typeof warning.details === 'object' ? warning.details.reason : null;
  const why = WEB_USAGE_REASON_TEXT[reason] ? `vox 웹 요약 ${WEB_USAGE_REASON_TEXT[reason]}` : '';
  const fresh = lastCheck.value;
  // 방금 끝까지 읽었다 — 월말 뒤에 읽었으면 이제 확정할 수 있는 웹 사용량이다
  const readNow = !!fresh && fresh.web_source === 'vox' && verdict.value.final;
  let text = `웹 테스트는 vox 웹 요약으로만 청구합니다${why ? `(${why})` : ''}. 이대로 확정하면 웹 테스트가 덜 청구될 수 있습니다.`;
  let next = null;
  if (readNow) {
    text = '방금 vox 웹 요약을 끝까지 읽었습니다. 정산에 남은 경고는 읽기 전에 계산한 것입니다.';
    if (status.value === 'review') {
      next = { action: 'recalculate', text: '검토 필요 정산은 [다시 계산]해야 웹 사용량이 들어갑니다.' };
    } else if (status.value === 'confirmed') {
      next = { action: 'reopen', text: '확정된 정산은 바뀌지 않습니다. 반영하려면 재수정합니다.' };
    }
  } else {
    text += ' [vox 건수 확인]으로 웹 요약까지 다시 읽습니다.';
  }
  return { blocking, text, next };
});

// ---------------------------------------------------------------------------
// DB 분해 막대 — AI 응대(수신) / AI 가 받기 전 끊김 / 웹 테스트 / 발신, 합 = DB 통화 수
// ---------------------------------------------------------------------------

const breakdown = computed(() => {
  const calls = props.detail.usage?.calls ?? {};
  // 분해는 상세의 사용량 한 벌로 센다(건수 대사 응답의 db_total 과는 상세를 다시 불러오면 같아진다)
  const total = firstNumber(calls.total, numbers.value.dbTotal);
  const web = num(calls.web);
  const outbound = num(calls.outbound);
  const canceled = num(calls.canceled_before_ai);
  const webView = webSourceView(props.detail.usage?.web_source);
  // ai_answered 에는 발신이 섞일 수 있어(월간 이용 리포트 트레이드오프) 나머지로 센다 — 막대 합이 DB 통화 수와 같다
  const ai =
    total !== null ? Math.max(0, total - (web ?? 0) - (outbound ?? 0) - (canceled ?? 0)) : num(calls.ai_answered);
  const parts = [
    { key: 'ai', label: 'AI 응대', n: ai, sub: '수신', tip: '수신 통화 중 AI 가 받은 통화(발신 제외)' },
    { key: 'canceled', label: 'AI 가 받기 전 끊김', n: canceled, sub: '', tip: 'AI 가 받기 전에 끝난 통화 — AI 응대 시간 없음' },
    { key: 'web', label: '웹 테스트', n: web, sub: webView.sub, tip: webView.tip },
    { key: 'outbound', label: '발신', n: outbound, sub: '', tip: 'AI 가 건 통화' },
  ];
  const sum = parts.reduce((acc, p) => acc + (p.n ?? 0), 0);
  const withPct = parts.map((p) => ({ ...p, pct: sum > 0 && p.n ? (p.n / sum) * 100 : 0 }));
  return {
    total,
    sum,
    parts: withPct,
    visible: withPct.filter((p) => p.pct > 0),
    ariaLabel: `DB 통화 ${formatCount(total, '건')}: ${parts.map((p) => `${p.label} ${formatNumber(p.n)}`).join(', ')}`,
  };
});

const notFinalView = computed(() => {
  const n = num(props.detail.usage?.calls?.not_final);
  const blocking = blockingOf(findWarning('NOT_FINAL_CALLS'));
  let tone = 'muted';
  if (n === 0) tone = 'ok';
  else if (n > 0) tone = blocking ? 'danger' : 'muted';
  return {
    n,
    blocking,
    tone,
    text: blocking ? '확정 전 확인 필요' : '내부·테스트 회사라 기록만 합니다',
  };
});

// ---------------------------------------------------------------------------
// 건수 대사 호출 — 화면에서 vox 를 부르는 유일한 곳
// ---------------------------------------------------------------------------

function describeError(err) {
  if (isCallApiUnimplemented(err)) return { text: UNIMPLEMENTED_TEXT, type: 'warning' };
  const status = getCallApiErrorStatus(err);
  const reason = reconErrorText(status, getCallApiErrorCode(err), getCallApiErrorBody(err)?.error?.message);
  if (reason) return { text: reason, type: 'warning' };
  return { text: parseCallApiError(err, 'vox 건수를 확인하지 못했습니다.'), type: 'error' };
}

async function safeReload() {
  try {
    await props.reload();
  } catch {
    // 상세를 다시 부르지 못한 것은 셸이 알린다 — 여기서는 방금 읽은 건수를 그대로 보인다
  }
}

function toMissingResult(res) {
  const meta = [fetchedText(res?.vox_live_fetched_at), res?.cached === true ? '캐시' : '']
    .filter(Boolean)
    .join(' · ');
  if (!res?.missing || typeof res.missing !== 'object') {
    // 못 받은 이유 — 통화가 많아 끝까지 못 읽음(too_many_calls) 등(회신 B17)
    return { unavailable: true, unavailableText: reconMissingUnavailableText(res?.missing_unavailable_reason), rows: [], meta };
  }
  const voxOnly = Array.isArray(res.missing.vox_only) ? res.missing.vox_only : [];
  const dbOnly = Array.isArray(res.missing.db_only) ? res.missing.db_only : [];
  const toRow = (side) => (item, i) => ({
    key: `${side}:${item?.vox_call_id ?? i}`,
    side,
    sideLabel: side === 'vox' ? 'vox 에만' : 'DB 에만',
    id: item?.vox_call_id ?? '—',
    at: formatKstDateTime(item?.created_at),
    type: CALL_TYPE_LABELS[item?.call_type] ?? item?.call_type ?? '—',
  });
  return {
    unavailable: false,
    voxOnly: voxOnly.length,
    dbOnly: dbOnly.length,
    rows: [...voxOnly.map(toRow('vox')), ...dbOnly.map(toRow('db'))],
    meta,
  };
}

/** 진행 중인 호출 — intent 가 겹치면 끝나길 기다렸다 이어 간다 */
let running = null;
/** 달·병원을 바꿔 이 탭이 내려간 뒤에는 이어지는 호출(상세 재조회·누락 확인)을 하지 않는다 */
let disposed = false;
onBeforeUnmount(() => {
  disposed = true;
});

async function request(missingIds) {
  const loading = missingIds ? missingLoading : countLoading;
  loading.value = true;
  checkError.value = null;
  const op = (async () => {
    try {
      const res = await props.api.getReconciliation(props.companyId, props.month, { missingIds });
      if (disposed) return false;
      lastCheck.value = res ?? null;
      // 건수가 바뀌었을 수 있다 — 누락 표는 누락 ID 를 다시 읽을 때만 새로 채운다
      missingResult.value = missingIds ? toMissingResult(res) : null;
      await safeReload();
      return true;
    } catch (err) {
      if (!disposed) checkError.value = describeError(err);
      return false;
    } finally {
      loading.value = false;
    }
  })();
  running = op;
  const ok = await op;
  if (running === op) running = null;
  return ok;
}

async function checkCount() {
  if (busy.value) return false;
  return request(false);
}

async function checkMissingIds() {
  if (busy.value) return false;
  const ok = await request(true);
  if (ok) {
    await nextTick();
    missingBlock.value?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }
  return ok;
}

// ---------------------------------------------------------------------------
// 셸 intent — 정산 탭 COUNT_MISMATCH 의 [누락 통화 ID 확인]
// ---------------------------------------------------------------------------

async function runMissingIdsIntent() {
  try {
    if (running) await running;
    // 지시를 받고 처음 마운트되는 중이면(immediate watch 는 setup 에서 돈다) 칸이 그려진 뒤에 옮긴다
    await nextTick();
    countSection.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    // 건수를 먼저 읽고, 지금도 다를 때만 누락 ID 를 훑는다(vox 통화 ID 페이지 조회 — 2-2 도 불일치일 때만
    // 그 버튼을 보인다). 판정은 방금 읽은 건수(lastCheck)로 다시 계산된다. 일치면 남은 경고가 이전 건수라는
    // 안내와 [다시 계산]·재수정 길이 보인다.
    const ok = await checkCount();
    if (ok && verdict.value.state === 'mismatch') await checkMissingIds();
  } finally {
    emit('intent-done');
  }
}

/** 셸 intent — 정산 탭 WEB_USAGE_UNVERIFIED 의 [vox 건수 확인(대사 탭)] */
async function runCountIntent() {
  try {
    if (running) await running;
    await nextTick();
    countSection.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    await checkCount();
  } finally {
    emit('intent-done');
  }
}

watch(
  () => props.intent?.nonce,
  (nonce) => {
    if (nonce === undefined || nonce === null) return;
    if (props.intent?.type === 'check-missing-ids') {
      void runMissingIdsIntent();
    } else if (props.intent?.type === 'check-count') {
      void runCountIntent();
    } else {
      // 이 탭이 모르는 지시는 받기만 한다(셸이 지시를 비우게)
      emit('intent-done');
    }
  },
  { immediate: true }
);

function goRecalculate() {
  emit('go-tab', 'settle', { type: 'focus-recalculate' });
}

function goWarnings() {
  emit('go-tab', 'settle', { type: 'focus-warnings' });
}

// ---------------------------------------------------------------------------
// 목 전용 — 대사 장면(다음 건수 확인·자동 마감의 vox 조회에 쓰인다)
// ---------------------------------------------------------------------------

const readScenario = () =>
  props.mock ? settlementMockControls.getReconScenario(props.companyId, props.month) : null;
const reconScenario = ref(readScenario());

// 탭을 다시 열 때마다 목 상태를 다시 읽는다(다른 화면의 개발용 조작이 바꿨을 수 있다)
onActivated(() => {
  reconScenario.value = readScenario();
});

function onScenarioChange(value) {
  settlementMockControls.setReconScenario(props.companyId, props.month, value);
  reconScenario.value = settlementMockControls.getReconScenario(props.companyId, props.month);
}
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

@include yellow-button;

// DB 분해 막대 색 — 목업 Recon 보드의 네 색을 antd 팔레트로
$seg-ai: $st-accent;
$seg-canceled: #faad14;
$seg-web: #8c8c8c;
$seg-outbound: #13c2c2;

.recon-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__dev {
    display: flex;
  }

  &__dev-select {
    min-width: 170px;
  }
}

.recon-card {
  @include st-card;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 10px;
  }

  &__title {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: $st-text;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-left: auto;
  }

  &__desc {
    margin: -4px 0 0;
    font-size: 12px;
    color: $st-muted;
  }

  &__alert {
    border-radius: 8px;
  }
}

.recon-verdict {
  @include st-pill($st-text-secondary, $st-bg, $st-border-strong);
  font-size: 12px;

  &.is-match {
    @include st-pill-tone($st-ok, $st-ok-bg, $st-ok-border);
  }

  &.is-mismatch {
    @include st-pill-tone($st-danger, $st-danger-bg, $st-danger-border);
  }
}

.recon-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
  gap: 12px;
}

.recon-tile {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid $st-border;
  border-radius: 10px;
  background: $st-bg;

  &.is-reference {
    border-style: dashed;
    background: #fff;
  }

  &__label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 6px;
    font-size: 12px;
    font-weight: 600;
    color: $st-text-secondary;
  }

  &__value {
    @include st-num;
    font-size: 22px;
    font-weight: 700;
    line-height: 1.3;
    color: $st-text;
  }

  &.is-empty &__value,
  &.is-reference &__value {
    color: $st-muted;
  }

  &__note {
    font-size: 12px;
    color: $st-text-secondary;
  }
}

.recon-pill {
  @include st-pill;

  &.is-reference,
  &.is-warn {
    @include st-pill-tone($st-warning, $st-warning-bg, $st-warning-border);
  }

  &.is-danger {
    @include st-pill-tone($st-danger, $st-danger-bg, $st-danger-border);
  }

  &.is-accent {
    @include st-pill-tone($st-accent, $st-accent-bg, $st-accent-border);
  }
}

.recon-code {
  padding: 0 6px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
  color: $st-text-secondary;
  font-size: 11.5px;
}

.recon-link {
  height: auto; // 전역 button { height: 40px } 덮어쓰기
  padding: 0;
  border: 0;
  background: none;
  color: $st-accent;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $st-accent;
    outline-offset: 2px;
  }
}

.recon-callout {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid $st-border-strong;
  border-radius: 10px;
  background: $st-bg;
  font-size: 12.5px;
  color: $st-text-secondary;

  &.is-danger {
    border-color: $st-danger-border;
    background: $st-danger-bg;
  }

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    color: $st-text;
  }

  &.is-danger &__head strong {
    color: $st-danger;
  }

  &__text,
  &__next {
    margin: 0;
  }

  &__next {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    color: $st-text;
  }
}

.recon-missing {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid $st-border;
  border-radius: 10px;
  outline: none;

  &__head {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 4px 10px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: $st-text;
  }

  &__meta {
    font-size: 12px;
    color: $st-muted;
  }

  &__summary {
    margin: 0;
    font-size: 12.5px;
    color: $st-text-secondary;
  }

  &__hint {
    margin-left: 6px;
    font-size: 12px;
    color: $st-muted;
  }

  &__empty {
    margin: 0;
    font-size: 12.5px;
    color: $st-muted;

    &.is-ok {
      display: flex;
      align-items: center;
      gap: 6px;
      color: $st-ok;
    }
  }
}

.recon-table {
  :deep(.ant-table) {
    font-size: 12.5px;
  }

  :deep(.ant-table-cell) {
    @include st-num;
  }
}

.recon-id {
  color: $st-text-secondary;
  font-size: 12px;
  white-space: nowrap;
}

.recon-breakdown {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 10px;
  }

  &__title {
    font-size: 13px;
    font-weight: 600;
    color: $st-text;
  }

  &__meta {
    font-size: 12px;
    color: $st-muted;
  }

  &__empty {
    margin: 0;
    font-size: 12.5px;
    color: $st-muted;
  }
}

.recon-bar {
  display: flex;
  gap: 2px;
  height: 14px;
  overflow: hidden;
  border-radius: 999px;
  background: #f5f5f5;

  &__seg {
    display: block;
    height: 100%;
    min-width: 3px;
  }
}

.recon-bar__seg,
.recon-legend__swatch {
  &.is-ai {
    background: $seg-ai;
  }

  &.is-canceled {
    background: $seg-canceled;
  }

  &.is-web {
    background: $seg-web;
  }

  &.is-outbound {
    background: $seg-outbound;
  }
}

.recon-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0;
  padding: 0;
  list-style: none;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: $st-text-secondary;
    cursor: help;
  }

  &__swatch {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }

  &__count {
    @include st-num;
    color: $st-text;
  }

  &__sub {
    font-size: 11.5px;
    color: $st-muted;
  }
}

.recon-notfinal {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  font-size: 12.5px;

  &__text {
    color: $st-text;
    font-weight: 600;
  }
}

.recon-chip {
  @include st-pill($st-text-secondary, #fff, $st-border-strong);
  padding: 1px 10px;
  font-size: 12px;

  b {
    @include st-num;
  }

  &.is-ok {
    @include st-pill-tone($st-ok, $st-ok-bg, $st-ok-border);
  }

  &.is-danger {
    @include st-pill-tone($st-danger, $st-danger-bg, $st-danger-border);
  }
}

.recon-footnote {
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid $st-border;
  font-size: 12px;
  color: $st-muted;
}
</style>
